import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { WarningAmberRounded as WarningAmberRoundedIcon } from '@mui/icons-material';
import { HabitSummary, HabitDefinition } from '../types/api';
import { getCategoryColor } from '../utils/habitColors';
import DraftChip from './DraftBanner';

interface HabitCardProps {
  habit: HabitSummary;
  habitDefinition?: HabitDefinition;
  onClick: (habit: HabitSummary) => void;
  weeks?: number;
  offTrackChildHabitCount?: number;
}

const HabitCard: React.FC<HabitCardProps> = ({
  habit,
  habitDefinition,
  onClick,
  weeks = 1,
  offTrackChildHabitCount = 0,
}) => {
  const habitName = habit.habitDisplayName;
  const isRangeLikeGoal = habitDefinition?.goal.type === 'RANGE' || habitDefinition?.goal.type === 'AVERAGE';
  const periodLabel = habitDefinition?.goal.type === 'AVERAGE' ? 'average' : 'total';
  const timeText = weeks > 1 ? `(past ${weeks} weeks ${periodLabel})` : `(past week ${periodLabel})`;

  // Helper to format numbers - show whole numbers without decimals
  const formatNumber = (num?: number | null) => {
    if (typeof num !== 'number' || !Number.isFinite(num)) return '0';
    return num % 1 === 0 ? num.toFixed(0) : num.toFixed(1);
  };

  const formatAmount = (num?: number | null) => {
    const formatted = formatNumber(num);
    return typeof num === 'number' && Number.isFinite(num) && num < 0 ? `(${formatted})` : formatted;
  };

  const getProgressColor = (percentage: number): 'success' | 'warning' | 'error' => {
    if (percentage >= 100) return 'success';
    if (percentage >= 80) return 'warning';
    return 'error';
  };

  const getScaledRangeBounds = () => {
    if (!isRangeLikeGoal || !habitDefinition || habitDefinition.goal.type === 'THRESHOLD') {
      return null;
    }

    const originalMin = habitDefinition.goal.min;
    const originalMax = habitDefinition.goal.max;
    if (habitDefinition.goal.type === 'AVERAGE') {
      return {
        scaledMin: originalMin,
        scaledMax: originalMax,
      };
    }
    const scalingFactor = originalMin > 0 ? habit.goalAmountScaled / originalMin : 1;

    return {
      scaledMin: habit.goalAmountScaled,
      scaledMax: originalMax * scalingFactor,
    };
  };

  const calculateRangeProgress = () => {
    if (!isRangeLikeGoal) {
      return {
        percentage: habit.percentageCompletedOverReportTimeInterval,
        displayText: `${formatNumber(habit.percentageCompletedOverReportTimeInterval)}% complete`,
        isNoData: false,
        isExceeded: false,
        progressBarValue: Math.min(habit.percentageCompletedOverReportTimeInterval, 100),
        color: getProgressColor(habit.percentageCompletedOverReportTimeInterval)
      };
    }

    // Use backend-scaled min and a proportional max derived from that same scale.
    const scaledRangeBounds = getScaledRangeBounds();
    if (!scaledRangeBounds) {
      return {
        percentage: habit.percentageCompletedOverReportTimeInterval,
        displayText: `${formatNumber(habit.percentageCompletedOverReportTimeInterval)}% complete`,
        isNoData: false,
        isExceeded: false,
        progressBarValue: Math.min(habit.percentageCompletedOverReportTimeInterval, 100),
        color: getProgressColor(habit.percentageCompletedOverReportTimeInterval)
      };
    }

    const { scaledMin, scaledMax } = scaledRangeBounds;
    const current = habit.currentAmountCompletedOverReportTimeInterval;
    const isAverageGoal = habitDefinition?.goal.type === 'AVERAGE';

    if (isAverageGoal && habit.hasEntriesOverReportTimeInterval === false) {
      return {
        percentage: 0,
        displayText: 'No data',
        isNoData: true,
        isExceeded: false,
        progressBarValue: 0,
        color: 'error' as const
      };
    }

    // Within range: 100% complete
    if (current >= scaledMin && current <= scaledMax) {
      return {
        percentage: 100,
        displayText: 'Within target range',
        isNoData: false,
        isExceeded: false,
        progressBarValue: 100,
        color: 'success' as const
      };
    }

    // Below min: show raw numbers
    if (current < scaledMin) {
      if (isAverageGoal) {
        return {
          percentage: 0,
          displayText: `Below minimum by ${formatNumber(scaledMin - current)} ${habit.goalUnit}`,
          isNoData: false,
          isExceeded: false,
          progressBarValue: 0,
          color: 'error' as const
        };
      }
      const percentageOfMin = (current / scaledMin) * 100;
      return {
        percentage: percentageOfMin,
        displayText: `${formatAmount(current)} of ${formatAmount(scaledMin)} ${habit.goalUnit} (minimum) completed`,
        isNoData: false,
        isExceeded: false,
        progressBarValue: Math.min(percentageOfMin, 100),
        color: 'error' as const
      };
    }

    // Above max: show raw numbers
    if (isAverageGoal) {
      return {
        percentage: 0,
        displayText: `Above maximum by ${formatNumber(current - scaledMax)} ${habit.goalUnit}`,
        isNoData: false,
        isExceeded: true,
        progressBarValue: 0,
        color: 'success' as const
      };
    }
    const percentageOfMax = (current / scaledMax) * 100;
    return {
      percentage: percentageOfMax,
      displayText: `${formatAmount(current)} ${habit.goalUnit} completed, exceeding ceiling of ${formatAmount(scaledMax)} ${habit.goalUnit}`,
      isNoData: false,
      isExceeded: true,
      progressBarValue: Math.min(percentageOfMax, 100),
      color: 'success' as const // Will be overridden by black color
    };
  };

  const progressInfo = calculateRangeProgress();
  const progressBarColor = progressInfo.isNoData ? 'inherit' : offTrackChildHabitCount > 0 ? 'warning' : progressInfo.color;

  const habitCard = (
    <Card 
      variant="outlined" 
      onClick={() => onClick(habit)}
      data-testid={`habit-card-${habit.habitId}`}
      sx={{ 
        height: '100%', 
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 2,
        },
      }}
    >
      {habitDefinition?.isDraft && (
        <Box
          sx={{
            bgcolor: 'grey.800',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            py: 0.5,
            px: 2,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
          }}
        >
          <DraftChip />
        </Box>
      )}
      <CardContent sx={habitDefinition?.isDraft ? { 
        pt: 1.5,
        opacity: 0.6,
        filter: 'grayscale(0.3)',
      } : {}}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {habitName}
          </Typography>
          <Chip
            label={habit.category}
            color={getCategoryColor(habit.category)}
            size="small"
          />
        </Box>
        
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Progress: {formatNumber(habit.currentAmountCompletedOverReportTimeInterval)} {isRangeLikeGoal 
              ? (() => {
                  const scaledRangeBounds = getScaledRangeBounds();
                  if (!scaledRangeBounds) return '/ 0 – 0';
                  return `/ ${formatAmount(scaledRangeBounds.scaledMin)} – ${formatAmount(scaledRangeBounds.scaledMax)}`;
                })()
              : `/ ${formatAmount(habit.goalAmountScaled)}`} {habit.goalUnit} {timeText}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progressInfo.progressBarValue}
            color={progressBarColor}
            sx={{ 
              height: 8, 
              borderRadius: 4,
              ...(progressInfo.isNoData && {
                backgroundColor: 'rgba(255, 255, 255, 0.18)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'rgba(255, 255, 255, 0.45)'
                }
              }),
              ...(progressInfo.isExceeded && {
                backgroundColor: 'rgba(255, 255, 255, 0.12)', // Gray background for unfilled portion
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#000000'
                }
              })
            }}
          />
          <Tooltip 
            title={progressInfo.isExceeded ? "% exceeded by is calculated by taking the average of the target goal range" : ""}
            arrow
            disableHoverListener={!progressInfo.isExceeded}
          >
            <Box
              component="span"
              sx={{
                mt: 1,
                display: 'inline-flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
              }}
            >
              <Typography variant="body2" color="text.secondary" component="span">
                {progressInfo.displayText}
              </Typography>
              {offTrackChildHabitCount > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary" component="span">
                    {`(Child Habit${offTrackChildHabitCount === 1 ? '' : 's'} Off-Track)`}
                  </Typography>
                  <WarningAmberRoundedIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                </>
              )}
            </Box>
          </Tooltip>
        </Box>

        {habit.entries && habit.entries.length > 0 && (
          <Box>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Recent Entries:
            </Typography>
            {habit.entries.slice(0, 3).map((entry, index) => (
              <Typography key={index} variant="body2" color="text.secondary">
                • {entry.count} {habit.goalUnit}
                {entry.comment && ` - ${entry.comment}`}
              </Typography>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return habitCard;
};

export default HabitCard;
